import {uniq} from 'ramda'
import {ProductType, TransferMethod} from '@modifi/enums'
import {
  COMPANY_BANK_STATEMENTS,
  SELLER_BANK_ACCOUNT_CURRENCIES,
  SELLER_BANK_ACCOUNT_TRANSFER_TYPE,
} from '@modifi/plugin-onboarding'
import {
  aFetchFinancingOptions,
  selectFinancingOptions,
  selectFinancingOptionsWhereLoaded,
} from '../../../../data/financing-options'
import {
  aFetchSellerBankAccounts,
  selectSellerBankAccountByExternalId,
  selectSellerBankAccountsWhereLoaded,
} from '../../../../data/seller-bank-accounts'
import {selectInputFieldNamesBySectionName} from '../../../../modules/input-fields'
import {SECTION_BANK_DETAILS} from '../../../../constants/sections'
import {selectDocumentsByInputFieldName} from '../../../../data/company-documents'
import {mapSellerBankAccountToFormData} from '../utils'
import {AppThunkAction} from '../../../../types/redux'

export const selectAssignedCurrenciesByExternalBankAccountId = (
  state: Record<string, any>,
  externalBankAccountId: string
): string[] => {
  const financingOptions = selectFinancingOptions(state)

  return (
    financingOptions
      .filter(option => option.sellerBankAccount?.externalId === externalBankAccountId)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map(option => option.externalId!)
      .reduce((result, currency) => {
        if (!result.includes(currency)) {
          result.push(currency)
        }
        return result
      }, [] as string[])
  )
}

export const selectAvailableSellerBankAccountCurrencies = (
  state: Record<string, any>,
  externalBankAccountId?: string
): {label: string; value: string}[] => {
  const financingOptions = selectFinancingOptions(state)

  return uniq(
    financingOptions
      .filter(
        option =>
          option.productType === ProductType.SELLER_FINANCING &&
          (!option.sellerBankAccount ||
            (externalBankAccountId &&
              option?.sellerBankAccount?.externalId === externalBankAccountId))
      )
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map(option => ({label: option.settlementCurrency!, value: option.externalId!}))
  )
}

export const selectInitializePagePending = (state: Record<string, any>): boolean => {
  const sellerBankAccountsWhereLoaded = selectSellerBankAccountsWhereLoaded(state)
  const financingOptionsWhereLoaded = selectFinancingOptionsWhereLoaded(state)
  return !sellerBankAccountsWhereLoaded || !financingOptionsWhereLoaded
}

export const selectInitialFormData = (
  state: Record<string, any>,
  externalBankAccountId?: string
): {defaultValues: Record<string, any>; initialValues: Record<string, any>} => {
  if (selectInitializePagePending(state)) {
    return {defaultValues: {}, initialValues: {}}
  }

  const inputFieldsToRender = selectInputFieldNamesBySectionName(state, SECTION_BANK_DETAILS)

  const defaultValues: Record<string, any> = {
    [SELLER_BANK_ACCOUNT_TRANSFER_TYPE]: TransferMethod.SEPA,
    [SELLER_BANK_ACCOUNT_CURRENCIES]: '',
  }
  const initialValues: Record<string, any> = {
    [COMPANY_BANK_STATEMENTS]: selectDocumentsByInputFieldName(state, COMPANY_BANK_STATEMENTS).map(
      ({fileId, documentType}) => ({fileId, documentType})
    ),
  }

  if (externalBankAccountId) {
    const sellerBankAccount = selectSellerBankAccountByExternalId(state, externalBankAccountId)

    if (sellerBankAccount) {
      Object.assign(
        initialValues,
        mapSellerBankAccountToFormData(sellerBankAccount, inputFieldsToRender)
      )
      initialValues.intermediaryBankCode = sellerBankAccount?.intermediaryBank?.bankCode || ''
    }

    if (inputFieldsToRender.includes(SELLER_BANK_ACCOUNT_CURRENCIES)) {
      // noinspection UnnecessaryLocalVariableJS
      const currencies = selectAssignedCurrenciesByExternalBankAccountId(
        state,
        externalBankAccountId
      )
      // workaround for wrong form data handling for empty currencies
      initialValues[SELLER_BANK_ACCOUNT_CURRENCIES] = currencies.length === 0 ? '' : currencies
    }
  }
  return {
    initialValues,
    defaultValues,
  }
}

export const aInitializePage =
  (): AppThunkAction<void> =>
  (dispatch): void => {
    dispatch(aFetchSellerBankAccounts())
    dispatch(aFetchFinancingOptions())
  }
